import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfaceNamespaceEntity } from '@unifyapps/defs/types/namespace';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { HitsEntity } from '@unifyapps/network/generated/models/hitsEntity';
import type { NamespaceSearchRequest } from '@unifyapps/network/generated/models/namespaceSearchRequest';
import { Op } from '@unifyapps/network/generated/models/op';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';

interface CommonNamespaceParams {
  interfaceId: string;
  locale: string;
}

export const getInterfaceCommonNamespaceQueryFilters = (params: CommonNamespaceParams) => {
  const { interfaceId, locale } = params;

  return {
    filter: {
      op: Op.AND,
      values: [
        {
          field: 'properties.interfaceId',
          op: Op.EQUAL,
          values: [interfaceId],
        },
        {
          field: 'properties.locale',
          op: Op.EQUAL,
          values: [locale],
        },
        {
          op: Op.OR,
          values: [
            {
              field: 'properties.interfaceComponentId',
              op: Op.MISSING,
            },
            {
              field: 'properties.interfaceComponentId',
              op: Op.EQUAL,
              values: [InterfaceHelper.commonNamespaceComponentId],
            },
          ],
        },
      ],
    },
    page: { limit: 100, offset: 0 },
  };
};

interface ComponentNamespaceParams extends CommonNamespaceParams {
  interfaceComponentId: string;
}

export const getInterfaceComponentNamespaceQueryFilters = (params: ComponentNamespaceParams) => {
  const { interfaceId, interfaceComponentId, locale } = params;

  return {
    filter: {
      op: Op.AND,
      values: [
        { field: 'properties.interfaceId', op: Op.EQUAL, values: [interfaceId] },
        { field: 'properties.interfaceComponentId', op: Op.EQUAL, values: [interfaceComponentId] },
        { field: 'properties.locale', op: Op.EQUAL, values: [locale] },
      ],
    },
    page: { limit: 100, offset: 0 },
  };
};

export const getInterfaceNamespaceQueryFilters = (params: ComponentNamespaceParams) => {
  if (params.interfaceComponentId === InterfaceHelper.commonNamespaceComponentId) {
    return getInterfaceCommonNamespaceQueryFilters(params);
  }
  return getInterfaceComponentNamespaceQueryFilters(params);
};

interface ComponentNamespacesByIdsParams {
  interfaceIds: string[];
  interfaceComponentIds: string[];
  locale: string;
}

/**
 * Multi-id variant of getInterfaceComponentNamespaceQueryFilters. Resolves namespaces
 * for an arbitrary set of pages in ONE search request using an IN filter so consumers
 * dealing with embedded-dependents responses don't need to fan out N queries.
 * Uses IN for interfaceId as well because embedded pages may belong to different interfaces.
 */
export const getInterfaceComponentNamespacesByIdsQueryFilters = (
  params: ComponentNamespacesByIdsParams,
) => {
  const { interfaceIds, interfaceComponentIds, locale } = params;

  return {
    filter: {
      op: Op.AND,
      values: [
        { field: 'properties.interfaceId', op: Op.IN, values: interfaceIds },
        { field: 'properties.interfaceComponentId', op: Op.IN, values: interfaceComponentIds },
        { field: 'properties.locale', op: Op.EQUAL, values: [locale] },
      ],
    },
    page: { limit: 100, offset: 0 },
  };
};

export const getInterfaceComponentNamespacesByIdsSearchRequest = (
  params: ComponentNamespacesByIdsParams,
): NamespaceSearchRequest => {
  const { interfaceIds, interfaceComponentIds, locale } = params;

  return {
    interfaceIds,
    componentIds: interfaceComponentIds,
    locale,
    page: { limit: 100, offset: 0 },
  };
};

export const getInterfaceCommonNamespaceSearchRequest = (
  params: CommonNamespaceParams,
): NamespaceSearchRequest => {
  const { interfaceId, locale } = params;

  return {
    interfaceIds: [interfaceId],
    locale,
    includeCommonNamespaces: true,
    page: { limit: 100, offset: 0 },
  };
};

/**
 * Collects unique, sorted page ids and interface ids from a list of pages.
 * Falls back to the provided parentInterfaceId when no page carries its own interfaceId.
 */
export function collectPageAndInterfaceIds(
  pages: (InterfacePageEntity | undefined)[],
  parentInterfaceId?: string,
) {
  const seenPages = new Set<string>();
  const seenInterfaces = new Set<string>();
  for (const p of pages) {
    if (p?.id) seenPages.add(p.id);
    if (p?.properties?.interfaceId) seenInterfaces.add(p.properties.interfaceId);
  }
  if (seenInterfaces.size === 0 && parentInterfaceId) seenInterfaces.add(parentInterfaceId);
  return {
    pageIds: Array.from(seenPages).sort(),
    interfaceIds: Array.from(seenInterfaces).sort(),
  };
}

/**
 * Converts a flat namespace search response into a map keyed by interfaceComponentId.
 */
export function bucketNamespacesByComponentId(
  namespaceHits: HitsEntity | undefined,
): Record<string, InterfaceNamespaceEntity | undefined> {
  const out: Record<string, InterfaceNamespaceEntity | undefined> = {};
  const objects = namespaceHits?.objects as InterfaceNamespaceEntity[] | undefined;
  objects?.forEach((ns) => {
    const componentId = (ns as { properties?: { interfaceComponentId?: string } }).properties
      ?.interfaceComponentId;
    if (componentId) out[componentId] = ns;
  });
  return out;
}

// fetch namespace only if interfaceDefaultLocale is available and different from current locale
export const getShouldFetchNamespace = (
  userLocale: string,
  interfaceRecord: InterfaceEntity | undefined,
) => {
  const interfaceDefaultLocale = InterfaceHelper.getDefaultLocale(interfaceRecord);
  return Boolean(interfaceDefaultLocale && interfaceDefaultLocale !== userLocale);
};
